import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { driverService } from '@services/driverService';
import { DriverFormData } from '@/types/driver';

export const DriverFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<DriverFormData>({
    full_name: '',
    email: '',
    whatsapp_phone_number: '',
    is_active: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && id) {
      loadDriver(id);
    }
  }, [id, isEditing]);

  const loadDriver = async (driverId: string) => {
    try {
      setLoading(true);
      const driver = await driverService.getById(parseInt(driverId));
      setFormData({
        full_name: driver.full_name,
        email: driver.email,
        whatsapp_phone_number: driver.whatsapp_phone_number,
        is_active: driver.is_active,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar motorista');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Nome completo é obrigatório';
    if (!formData.email.trim()) newErrors.email = 'E-mail é obrigatório';
    if (!formData.whatsapp_phone_number.trim()) newErrors.whatsapp_phone_number = 'Telefone WhatsApp é obrigatório';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      setError(null);
      if (isEditing && id) {
        await driverService.update(parseInt(id), formData);
      } else {
        await driverService.create(formData);
      }
      navigate('/settings/transport/drivers');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof DriverFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/settings/transport/drivers')} icon={<ArrowLeft size={20} />} />
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Motorista' : 'Novo Motorista'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Atualize os dados do motorista' : 'Preencha os dados para cadastrar um novo motorista'}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      <Card>
        <CardHeader title="Dados do Motorista" />
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome Completo *</label>
            <Input
              value={formData.full_name}
              onChange={(e) => handleChange('full_name', e.target.value)}
              placeholder="Nome completo do motorista"
              className={errors.full_name ? 'border-red-500' : ''}
            />
            {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-mail *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="email@exemplo.com"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Telefone WhatsApp *</label>
            <Input
              value={formData.whatsapp_phone_number}
              onChange={(e) => handleChange('whatsapp_phone_number', e.target.value)}
              placeholder="(11) 99999-9999"
              className={errors.whatsapp_phone_number ? 'border-red-500' : ''}
            />
            {errors.whatsapp_phone_number && <p className="text-red-500 text-sm mt-1">{errors.whatsapp_phone_number}</p>}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Ativo</label>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={() => navigate('/settings/transport/drivers')}>
          Cancelar
        </Button>
        <Button onClick={handleSave} disabled={saving} icon={<Save size={18} />}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </div>
  );
};
